/* ================================================================
   LF TEXTBOX — Design Checkpoint 1 (B10)
   ----------------------------------------------------------------
   Pokémon-style in-game textbox with type-on animation.

   USAGE (HTML):
     <!-- Basic textbox — static, no type-on -->
     <div class="lf-textbox lf-textbox--info">
       <div class="lf-textbox-inner">Your text here.</div>
     </div>

     <!-- With type-on animation (triggers on scroll-enter) -->
     <div class="lf-textbox lf-textbox--dialog" data-typeon="true" data-typeon-speed="35">
       <div class="lf-textbox-inner">Your text here.</div>
     </div>

   The .lf-textbox-inner wrapper is required for type-on elements —
   JS replaces its content character by character.

   AUTO-INIT: any element with [data-textbox] is automatically
   wrapped in .lf-textbox markup by this script, so existing
   content in templates doesn't need manual class changes.

   PERFORMANCE: uses IntersectionObserver (threshold 0.2) so type-on
   only fires when the box is actually visible on screen. One observer
   instance watches all textboxes — O(1) overhead per scroll tick.
================================================================ */
(function () {
  'use strict';

  if (window.__lfTextboxBound) return;
  window.__lfTextboxBound = true;

  var DEFAULT_SPEED   = 40;   /* ms per character */
  var ENTER_THRESHOLD = 0.15; /* fraction visible before animation starts */

  var reducedMotion = false;
  try {
    reducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* ── Auto-wrap [data-textbox] elements ── */
  function autoWrap() {
    var els = document.querySelectorAll('[data-textbox]:not(.lf-textbox)');
    els.forEach(function (el) {
      var variant   = el.getAttribute('data-textbox-variant') || 'info';
      var typeon    = el.hasAttribute('data-typeon') ? el.getAttribute('data-typeon') : null;
      var speed     = el.getAttribute('data-typeon-speed') || String(DEFAULT_SPEED);

      /* Build wrapper */
      var wrapper = document.createElement('div');
      wrapper.className = 'lf-textbox lf-textbox--' + variant;
      if (typeon === 'true') {
        wrapper.setAttribute('data-typeon', 'true');
        wrapper.setAttribute('data-typeon-speed', speed);
      }

      /* Wrap content in .lf-textbox-inner */
      var inner = document.createElement('div');
      inner.className = 'lf-textbox-inner';
      /* Move children */
      while (el.firstChild) inner.appendChild(el.firstChild);
      wrapper.appendChild(inner);

      /* Replace in DOM */
      el.parentNode.insertBefore(wrapper, el);
      el.parentNode.removeChild(el);
    });
  }

  /* ── Type-on engine ── */
  function typeOn(box) {
    if (box.__lfTyping) return;
    box.__lfTyping = true;

    var inner = box.querySelector('.lf-textbox-inner');
    if (!inner) { box.classList.add('is-done'); return; }

    var speed = parseInt(box.getAttribute('data-typeon-speed') || String(DEFAULT_SPEED), 10) || DEFAULT_SPEED;

    /* Grab full text — getTextContent is good for plain text,
       but we need to preserve the HTML structure for spans/em/etc.
       Strategy: flatten to text for animation, then swap back to HTML. */
    var fullHTML = inner.innerHTML;
    var fullText = inner.textContent || '';

    /* If reduced motion: skip straight to done */
    if (reducedMotion || fullText.length === 0) {
      inner.innerHTML = fullHTML;
      box.classList.add('is-done');
      return;
    }

    /* Slide-in entrance */
    box.classList.add('is-entering');
    box.classList.add('is-typing');

    /* Replace inner with a span we'll fill character by character */
    var display = document.createElement('span');
    inner.innerHTML = '';
    inner.appendChild(display);

    var idx = 0;
    function tick() {
      if (idx >= fullText.length) {
        /* Restore full HTML (in case there were inline tags) */
        inner.innerHTML = fullHTML;
        box.classList.remove('is-typing');
        box.classList.add('is-done');
        return;
      }
      display.textContent = fullText.slice(0, idx + 1);
      idx++;
      setTimeout(tick, speed);
    }

    /* Small delay after entrance before typing starts */
    setTimeout(tick, 120);
  }

  /* ── IntersectionObserver — triggers entrance + type-on ── */
  function initObserver() {
    var boxes = document.querySelectorAll('.lf-textbox[data-typeon="true"]');
    if (!boxes.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !entry.target.__lfTyping) {
          typeOn(entry.target);
          /* Unobserve after firing — each box types once */
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: ENTER_THRESHOLD });

    boxes.forEach(function (box) { observer.observe(box); });
  }

  /* ── Init ── */
  function init() {
    autoWrap();
    /* initObserver after wrapping so newly-created .lf-textbox elements
       are included in the query. */
    if (typeof IntersectionObserver !== 'undefined') {
      initObserver();
    } else {
      /* Fallback for very old browsers: type-on immediately for all visible */
      var boxes = document.querySelectorAll('.lf-textbox[data-typeon="true"]');
      boxes.forEach(typeOn);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
